import type { BlogArticle } from '../../types/blog'
import { BLOG_DATE_MODIFIED, BLOG_DATE_PUBLISHED } from './blogCommon'

export const withoutInsuranceArticle: BlogArticle = {
  slug: 'dental-treatment-uk-without-insurance',
  theme: 'teal',
  datePublished: BLOG_DATE_PUBLISHED,
  dateModified: BLOG_DATE_MODIFIED,
  category: { en: 'Tips', ua: 'Поради', ru: 'Советы', ro: 'Sfaturi' },
  readTime: { en: '8 min read', ua: '8 хв читання', ru: '8 мин чтения', ro: '8 min citire' },
  seoTitle: {
    en: 'Dental Treatment in the UK Without Insurance: 2026 Guide | Dentico Clinic',
    ua: 'Лікування зубів у Великій Британії без страховки: гід 2026 | Dentico Clinic',
    ru: 'Лечение зубов в Великобритании без страховки: гид 2026 | Dentico Clinic',
    ro: 'Tratament dentar în Marea Britanie fără asigurare: ghid 2026 | Dentico Clinic',
  },
  seoDescription: {
    en: 'How to get dental treatment in the UK without insurance in 2026: private pay-as-you-go care, typical costs, finance options and how to plan ahead.',
    ua: 'Як лікувати зуби у Великій Британії без страховки у 2026 році: приватна оплата за візит, орієнтовна вартість, розстрочка і планування лікування.',
    ru: 'Как лечить зубы в Великобритании без страховки в 2026 году: частная оплата за визит, ориентировочная стоимость, рассрочка и планирование лечения.',
    ro: 'Cum obțineți tratament dentar în Marea Britanie fără asigurare în 2026: plată per vizită, costuri orientative, finanțare și planificare.',
  },
  h1: {
    en: 'Dental treatment in the UK without insurance: a practical 2026 guide',
    ua: 'Лікування зубів у Великій Британії без страховки: практичний гід 2026',
    ru: 'Лечение зубов в Великобритании без страховки: практический гид 2026',
    ro: 'Tratament dentar în Marea Britanie fără asigurare: ghid practic 2026',
  },
  cardTitle: {
    en: 'Dental treatment in the UK without insurance',
    ua: 'Лікування зубів у Великій Британії без страховки',
    ru: 'Лечение зубов в Великобритании без страховки',
    ro: 'Tratament dentar în Marea Britanie fără asigurare',
  },
  cardExcerpt: {
    en: 'You do not need dental insurance to see a private dentist in London. How pay-as-you-go care works, what it costs and how to plan ahead.',
    ua: 'Щоб піти до приватного стоматолога в Лондоні, страховка не потрібна. Як працює оплата за візит, скільки це коштує і як планувати лікування.',
    ru: 'Чтобы пойти к частному стоматологу в Лондоне, страховка не нужна. Как работает оплата за визит, сколько это стоит и как планировать лечение.',
    ro: 'Nu aveți nevoie de asigurare pentru a merge la un dentist privat în Londra. Cum funcționează plata per vizită, cât costă și cum planificați.',
  },
  intro: {
    en: 'Many people arriving in the UK are surprised to learn that dental insurance is not required to see a dentist. Most private clinics in London work on a pay-as-you-go basis: you pay for each appointment or treatment as you receive it. This guide explains how that works in 2026, what treatments typically cost, and how to keep your dental care predictable and affordable.',
    ua: 'Багато людей, які приїжджають до Великої Британії, дивуються, що для візиту до стоматолога страховка не потрібна. Більшість приватних клінік Лондона працюють за принципом оплати за візит: ви платите за кожен прийом чи процедуру в момент отримання. У цьому гіді пояснюємо, як це працює у 2026 році, скільки орієнтовно коштує лікування і як зробити витрати на зуби передбачуваними.',
    ru: 'Многие люди, приезжающие в Великобританию, удивляются, что для визита к стоматологу страховка не нужна. Большинство частных клиник Лондона работают по принципу оплаты за визит: вы платите за каждый приём или процедуру в момент получения. В этом гиде объясняем, как это работает в 2026 году, сколько ориентировочно стоит лечение и как сделать расходы на зубы предсказуемыми.',
    ro: 'Mulți oameni care ajung în Marea Britanie sunt surprinși să afle că nu au nevoie de asigurare pentru a merge la dentist. Majoritatea clinicilor private din Londra funcționează cu plată per vizită: plătiți fiecare programare sau tratament când îl primiți. Acest ghid explică cum funcționează în 2026, cât costă de obicei tratamentele și cum să păstrați îngrijirea dentară previzibilă și accesibilă.',
  },
  sections: [
    {
      heading: {
        en: 'Your options without dental insurance',
        ua: 'Ваші варіанти без стоматологічної страховки',
        ru: 'Ваши варианты без стоматологической страховки',
        ro: 'Opțiunile dumneavoastră fără asigurare dentară',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              title: { en: 'NHS dentistry:', ua: 'Стоматологія NHS:', ru: 'Стоматология NHS:', ro: 'Stomatologia NHS:' },
              text: {
                en: 'subsidised care charged in fixed bands, if you can find a practice accepting new patients. Read our [NHS vs private dentistry guide](/blog/nhs-vs-private-dentistry-uk).',
                ua: 'субсидована допомога за фіксованими тарифами, якщо вдасться знайти практику, що приймає нових пацієнтів. Читайте наш [гід NHS чи приватна стоматологія](/blog/nhs-vs-private-dentistry-uk).',
                ru: 'субсидированная помощь по фиксированным тарифам, если удастся найти практику, принимающую новых пациентов. Читайте наш [гид NHS или частная стоматология](/blog/nhs-vs-private-dentistry-uk).',
                ro: 'îngrijire subvenționată, taxată în benzi fixe, dacă găsiți un cabinet care acceptă pacienți noi. Citiți [ghidul NHS vs stomatologie privată](/blog/nhs-vs-private-dentistry-uk).',
              },
            },
            {
              title: { en: 'Private pay-as-you-go:', ua: 'Приватно з оплатою за візит:', ru: 'Частно с оплатой за визит:', ro: 'Privat, cu plată per vizită:' },
              text: {
                en: 'no registration or insurance needed; you see the price list in advance and pay per appointment.',
                ua: 'без реєстрації і страховки; ви заздалегідь бачите прайс і платите за кожен прийом.',
                ru: 'без регистрации и страховки; вы заранее видите прайс и платите за каждый приём.',
                ro: 'fără înregistrare sau asigurare; vedeți lista de prețuri în avans și plătiți per programare.',
              },
            },
            {
              title: { en: 'Finance for larger treatment:', ua: 'Розстрочка для більшого лікування:', ru: 'Рассрочка для крупного лечения:', ro: 'Finanțare pentru tratamente mari:' },
              text: {
                en: 'some clinics, including Dentico Clinic, offer 0% finance for up to 12 months on selected treatments, subject to approval.',
                ua: 'деякі клініки, зокрема Dentico Clinic, пропонують 0% розстрочку до 12 місяців на вибрані види лікування за умови схвалення.',
                ru: 'некоторые клиники, включая Dentico Clinic, предлагают 0% рассрочку до 12 месяцев на выбранные виды лечения при условии одобрения.',
                ro: 'unele clinici, inclusiv Dentico Clinic, oferă finanțare 0% până la 12 luni pentru anumite tratamente, sub rezerva aprobării.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'What private dental treatment typically involves',
        ua: 'Що зазвичай включає приватне лікування',
        ru: 'Что обычно включает частное лечение',
        ro: 'Ce implică de obicei tratamentul privat',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'Most patients start with an examination, often with X-rays, so the dentist can see what is needed and give you a written plan with costs. Common treatments paid for without insurance include:',
            ua: 'Більшість пацієнтів починають з огляду, часто зі знімками, щоб лікар побачив, що потрібно, і дав письмовий план з вартістю. Найпоширеніші процедури, які оплачують без страховки:',
            ru: 'Большинство пациентов начинают с осмотра, часто со снимками, чтобы врач увидел, что нужно, и дал письменный план со стоимостью. Самые распространённые процедуры, которые оплачивают без страховки:',
            ro: 'Majoritatea pacienților încep cu o examinare, adesea cu radiografii, pentru ca medicul să vadă ce este necesar și să ofere un plan scris cu costuri. Tratamente frecvente plătite fără asigurare:',
          },
        },
        {
          type: 'list',
          items: [
            {
              title: { en: 'Hygiene visits:', ua: 'Професійна гігієна:', ru: 'Профессиональная гигиена:', ro: 'Igienizare:' },
              text: {
                en: 'regular [professional dental hygiene](/services/hygiene) helps prevent gum disease and more costly treatment later.',
                ua: 'регулярна [професійна гігієна порожнини рота](/services/hygiene) допомагає запобігти захворюванням ясен і дорожчому лікуванню в майбутньому.',
                ru: 'регулярная [профессиональная гигиена полости рта](/services/hygiene) помогает предотвратить заболевания дёсен и более дорогое лечение в будущем.',
                ro: '[igienizarea dentară profesională](/services/hygiene) regulată ajută la prevenirea bolilor gingivale și a tratamentelor costisitoare ulterioare.',
              },
            },
            {
              title: { en: 'Fillings:', ua: 'Пломби:', ru: 'Пломбы:', ro: 'Plombe:' },
              text: {
                en: 'treating decay early with [tooth-coloured fillings](/services/fillings) is simpler and cheaper than waiting.',
                ua: 'раннє лікування карієсу [естетичними пломбами](/services/fillings) простіше і дешевше, ніж очікування.',
                ru: 'раннее лечение кариеса [эстетическими пломбами](/services/fillings) проще и дешевле, чем ожидание.',
                ro: 'tratarea timpurie a cariilor cu [plombe de culoarea dintelui](/services/fillings) este mai simplă și mai ieftină decât așteptarea.',
              },
            },
            {
              title: { en: 'Root canal treatment:', ua: 'Лікування каналів:', ru: 'Лечение каналов:', ro: 'Tratament de canal:' },
              text: {
                en: 'when decay reaches the nerve, [root canal treatment](/root-canal-london) can often save the tooth.',
                ua: 'коли карієс досягає нерва, [лікування каналів](/root-canal-london) часто дозволяє зберегти зуб.',
                ru: 'когда кариес достигает нерва, [лечение каналов](/root-canal-london) часто позволяет сохранить зуб.',
                ro: 'când caria ajunge la nerv, [tratamentul de canal](/root-canal-london) poate salva adesea dintele.',
              },
            },
            {
              title: { en: 'Urgent appointments:', ua: 'Невідкладні прийоми:', ru: 'Неотложные приёмы:', ro: 'Programări urgente:' },
              text: {
                en: 'for toothache, swelling or a broken tooth, an [emergency dentist](/emergency-dentist-london) can usually see you quickly.',
                ua: 'при зубному болю, набряку чи зламаному зубі [невідкладний стоматолог](/emergency-dentist-london) зазвичай може прийняти швидко.',
                ru: 'при зубной боли, отёке или сломанном зубе [неотложный стоматолог](/emergency-dentist-london) обычно может принять быстро.',
                ro: 'pentru durere de dinți, umflături sau un dinte rupt, un [dentist de urgență](/emergency-dentist-london) vă poate vedea de obicei rapid.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'How to keep costs predictable',
        ua: 'Як зробити витрати передбачуваними',
        ru: 'Как сделать расходы предсказуемыми',
        ro: 'Cum păstrați costurile previzibile',
      },
      blocks: [
        {
          type: 'steps',
          items: [
            {
              title: { en: 'Check published prices', ua: 'Перегляньте опубліковані ціни', ru: 'Посмотрите опубликованные цены', ro: 'Verificați prețurile publicate' },
              text: {
                en: 'Choose a clinic that shows its fees openly. You can see ours on the [Dentico Clinic prices page](/prices).',
                ua: 'Обирайте клініку, яка відкрито показує ціни. Наші — на [сторінці цін Dentico Clinic](/prices).',
                ru: 'Выбирайте клинику, которая открыто показывает цены. Наши — на [странице цен Dentico Clinic](/prices).',
                ro: 'Alegeți o clinică care își afișează deschis tarifele. Le vedeți pe ale noastre pe [pagina de prețuri Dentico Clinic](/prices).',
              },
            },
            {
              title: { en: 'Ask for a written plan', ua: 'Попросіть письмовий план', ru: 'Попросите письменный план', ro: 'Cereți un plan scris' },
              text: {
                en: 'An itemised plan lets you prioritise urgent work and schedule the rest over time.',
                ua: 'План із розписаною вартістю дозволяє спершу зробити термінове, а решту розподілити в часі.',
                ru: 'План с расписанной стоимостью позволяет сначала сделать срочное, а остальное распределить во времени.',
                ro: 'Un plan detaliat vă permite să prioritizați lucrările urgente și să programați restul în timp.',
              },
            },
            {
              title: { en: 'Do not skip prevention', ua: 'Не нехтуйте профілактикою', ru: 'Не пренебрегайте профилактикой', ro: 'Nu săriți peste prevenție' },
              text: {
                en: 'Regular check-ups and hygiene visits are the most reliable way to avoid larger bills later.',
                ua: 'Регулярні огляди і гігієна — найнадійніший спосіб уникнути великих витрат у майбутньому.',
                ru: 'Регулярные осмотры и гигиена — самый надёжный способ избежать крупных расходов в будущем.',
                ro: 'Controalele și igienizările regulate sunt cea mai sigură cale de a evita facturi mari ulterior.',
              },
            },
          ],
        },
      ],
    },
    {
      heading: {
        en: 'Seeing a dentist at Dentico Clinic without insurance',
        ua: 'Прийом у Dentico Clinic без страховки',
        ru: 'Приём в Dentico Clinic без страховки',
        ro: 'Programare la Dentico Clinic fără asigurare',
      },
      blocks: [
        {
          type: 'p',
          text: {
            en: 'At Dentico Clinic in Angel you can book directly, without insurance or registration. We explain your options in plain language and give you costs before treatment starts. Book online, message us on WhatsApp, or [contact the clinic](/contact) — we speak English, Ukrainian, Russian and Romanian.',
            ua: 'У Dentico Clinic в Angel можна записатися напряму, без страховки і реєстрації. Ми зрозуміло пояснюємо варіанти і називаємо вартість до початку лікування. Запишіться онлайн, напишіть у WhatsApp або [зв’яжіться з клінікою](/contact) — ми говоримо українською, англійською, російською і румунською.',
            ru: 'В Dentico Clinic в Angel можно записаться напрямую, без страховки и регистрации. Мы понятно объясняем варианты и называем стоимость до начала лечения. Запишитесь онлайн, напишите в WhatsApp или [свяжитесь с клиникой](/contact) — мы говорим на русском, английском, украинском и румынском.',
            ro: 'La Dentico Clinic din Angel vă puteți programa direct, fără asigurare sau înregistrare. Vă explicăm opțiunile pe înțeles și vă comunicăm costurile înainte de tratament. Programați-vă online, scrieți-ne pe WhatsApp sau [contactați clinica](/contact) — vorbim română, engleză, ucraineană și rusă.',
          },
        },
      ],
    },
  ],
  faqs: [
    {
      q: { en: 'Do I need insurance to see a private dentist in the UK?', ua: 'Чи потрібна страховка для візиту до приватного стоматолога у Великій Британії?', ru: 'Нужна ли страховка для визита к частному стоматологу в Великобритании?', ro: 'Am nevoie de asigurare pentru un dentist privat în Marea Britanie?' },
      a: {
        en: 'No. Most private clinics accept patients on a pay-as-you-go basis without insurance.',
        ua: 'Ні. Більшість приватних клінік приймають пацієнтів з оплатою за візит без страховки.',
        ru: 'Нет. Большинство частных клиник принимают пациентов с оплатой за визит без страховки.',
        ro: 'Nu. Majoritatea clinicilor private acceptă pacienți cu plată per vizită, fără asigurare.',
      },
    },
    {
      q: { en: 'Can I be seen if I have just arrived in the UK?', ua: 'Чи можуть мене прийняти, якщо я щойно приїхав до Великої Британії?', ru: 'Могут ли меня принять, если я только приехал в Великобританию?', ro: 'Pot fi consultat dacă tocmai am ajuns în Marea Britanie?' },
      a: {
        en: 'Yes. Private clinics do not require a GP, NHS number or UK insurance to book an appointment.',
        ua: 'Так. Приватні клініки не вимагають GP, номера NHS чи британської страховки для запису.',
        ru: 'Да. Частные клиники не требуют GP, номера NHS или британской страховки для записи.',
        ro: 'Da. Clinicile private nu cer medic de familie (GP), număr NHS sau asigurare britanică pentru programare.',
      },
    },
    {
      q: { en: 'How do I know the cost before treatment?', ua: 'Як дізнатися вартість до лікування?', ru: 'Как узнать стоимость до лечения?', ro: 'Cum aflu costul înainte de tratament?' },
      a: {
        en: 'Check the clinic’s published prices and ask for a written treatment plan after your examination.',
        ua: 'Перегляньте опубліковані ціни клініки і попросіть письмовий план лікування після огляду.',
        ru: 'Посмотрите опубликованные цены клиники и попросите письменный план лечения после осмотра.',
        ro: 'Consultați prețurile publicate ale clinicii și cereți un plan de tratament scris după examinare.',
      },
    },
    {
      q: { en: 'Is finance available for dental treatment?', ua: 'Чи є розстрочка на лікування зубів?', ru: 'Есть ли рассрочка на лечение зубов?', ro: 'Există finanțare pentru tratamentul dentar?' },
      a: {
        en: 'Dentico Clinic offers 0% finance for up to 12 months on selected treatments, subject to approval. Terms and conditions apply.',
        ua: 'Dentico Clinic пропонує 0% розстрочку до 12 місяців на вибрані види лікування за умови схвалення. Застосовуються умови.',
        ru: 'Dentico Clinic предлагает 0% рассрочку до 12 месяцев на выбранные виды лечения при условии одобрения. Применяются условия.',
        ro: 'Dentico Clinic oferă finanțare 0% până la 12 luni pentru anumite tratamente, sub rezerva aprobării. Se aplică termeni și condiții.',
      },
    },
    {
      q: { en: 'What if I have dental pain and no dentist?', ua: 'Що робити, якщо болить зуб, а стоматолога немає?', ru: 'Что делать, если болит зуб, а стоматолога нет?', ro: 'Ce fac dacă am durere dentară și nu am dentist?' },
      a: {
        en: 'Contact a private clinic for an emergency appointment or call NHS 111. Call 999 if swelling affects breathing or swallowing.',
        ua: 'Зверніться до приватної клініки на невідкладний прийом або зателефонуйте NHS 111. Телефонуйте 999, якщо набряк заважає дихати чи ковтати.',
        ru: 'Обратитесь в частную клинику на неотложный приём или позвоните NHS 111. Звоните 999, если отёк мешает дышать или глотать.',
        ro: 'Contactați o clinică privată pentru o programare de urgență sau sunați la NHS 111. Sunați la 999 dacă umflătura afectează respirația sau înghițirea.',
      },
    },
  ],
  readAlso: [
    {
      to: '/blog/nhs-vs-private-dentistry-uk',
      label: { en: 'NHS vs Private Dentistry', ua: 'NHS чи приватна стоматологія', ru: 'NHS или частная стоматология', ro: 'NHS vs stomatologie privată' },
    },
    {
      to: '/prices',
      label: { en: 'Dentico Clinic Price List', ua: 'Прайс-лист Dentico Clinic', ru: 'Прайс-лист Dentico Clinic', ro: 'Lista de prețuri Dentico Clinic' },
    },
  ],
}
